import type { CursoAcademy } from './academyApi';

/**
 * Formateo y derivaciones de Academia Atlas — todo lo que era presentación y
 * vivía mezclado con los datos en `cursosCatalogo.ts`
 * (ADR-2026-08-25-la-academia-vive-en-supabase).
 *
 * Son funciones puras sobre los tipos de `academyApi`: no leen la base ni saben
 * de React.
 */

/**
 * "6:20" a partir de segundos, y "1:04:00" cuando pasa la hora. Los minutos van
 * con dos dígitos sólo cuando hay horas adelante, que es como los muestra
 * cualquier reproductor.
 */
export function formatearDuracion(segundos: number): string {
  const h = Math.floor(segundos / 3600);
  const m = Math.floor((segundos % 3600) / 60);
  const s = segundos % 60;
  const mm = h > 0 ? String(m).padStart(2, '0') : String(m);
  return h > 0
    ? `${h}:${mm}:${String(s).padStart(2, '0')}`
    : `${mm}:${String(s).padStart(2, '0')}`;
}

export function duracionTotalSeg(curso: CursoAcademy): number {
  return curso.lecciones.reduce((total, l) => total + l.duracionSeg, 0);
}

/**
 * "48 min", y "5 h 32 min" cuando pasa la hora. Redondea al minuto porque el
 * segundo exacto no le dice nada a quien está eligiendo qué mirar, y nunca baja
 * de "1 min" para no mostrar "0 min" en un curso que sí dura algo.
 */
export function duracionTotalLabel(curso: CursoAcademy): string {
  return duracionLabel(duracionTotalSeg(curso));
}

/** Lo mismo que `duracionTotalLabel` sobre segundos sueltos (p. ej. varios cursos). */
export function duracionLabel(segundos: number): string {
  const minutos = Math.max(1, Math.round(segundos / 60));
  if (minutos < 60) return `${minutos} min`;
  const horas = Math.floor(minutos / 60);
  const resto = minutos % 60;
  return resto === 0 ? `${horas} h` : `${horas} h ${resto} min`;
}

/**
 * "Curso 0: Primeros pasos en Atlas" — ADR-2026-08-24-cursos-numerados-desde-cero.
 *
 * El número sale de la POSICIÓN en la lista que se está mostrando, no de un
 * campo del curso: ahora ese orden lo decide `academy_cursos.orden` en la base,
 * así que reordenar allá renumera acá sin tocar nada.
 *
 * Un curso que no esté en la lista va sin número en vez de mostrar "Curso -1:".
 */
export function tituloNumerado(curso: CursoAcademy, cursos: CursoAcademy[]): string {
  const n = cursos.findIndex((c) => c.id === curso.id);
  return n < 0 ? curso.titulo : `Curso ${n}: ${curso.titulo}`;
}

/**
 * URL del embed. `rel=0` para que las sugerencias del final sean del mismo canal
 * y no manden al usuario a mirar otra cosa; `modestbranding=1` baja el logo.
 */
export function urlEmbedYoutube(youtubeId: string): string {
  const params = new URLSearchParams({
    rel: '0',
    modestbranding: '1',
    playsinline: '1',
  });
  return `https://www.youtube-nocookie.com/embed/${youtubeId}?${params}`;
}
